/**
 * ============================================================================
 * useKb.ts — Hooks TanStack Query para a Base de Conhecimento (KB)
 * ----------------------------------------------------------------------------
 * Consome as RPCs kb_* (SECURITY DEFINER) criadas no F1 Sprint 2 - Bloco 1/3.
 * Todas as RPCs já validam tenant via get_validated_tenant_id e usam
 * RAISE EXCEPTION para atomicidade — o hook apenas encaminha o retorno/erro.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.0.0 - Versão inicial (spaces, collections, pages, search).
 * ============================================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

// ---------------------------------------------------------------------------
// Tipos utilitários (espelham enums do DB)
// ---------------------------------------------------------------------------
export type KbAiVisibility = "public" | "tenant" | "none" | "inherit";
export type KbDataClassification = "public" | "internal" | "confidential" | "restricted";
export type KbPageStatus = "draft" | "in_review" | "published" | "archived";

// ---------------------------------------------------------------------------
// Chaves de cache
// ---------------------------------------------------------------------------
export const kbKeys = {
  all: ["kb"] as const,
  spaces: () => [...kbKeys.all, "spaces"] as const,
  pagesTree: (spaceId: string, collectionId?: string | null) =>
    [...kbKeys.all, "tree", spaceId, collectionId ?? null] as const,
  collections: (spaceId: string) =>
    [...kbKeys.all, "collections", spaceId] as const,
  page: (pageId: string) => [...kbKeys.all, "page", pageId] as const,
  search: (query: string, spaceId?: string | null) =>
    [...kbKeys.all, "search", query, spaceId ?? null] as const,
};

// ===========================================================================
// SPACES
// ===========================================================================

/** Lista todos os spaces do tenant ativo. */
export function useKbSpaces() {
  return useQuery({
    queryKey: kbKeys.spaces(),
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_list_spaces", {});
      if (error) {
        logger.error("useKbSpaces", "erro", error);
        throw error;
      }
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

/** Cria um novo space no tenant ativo. */
export function useCreateKbSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      name: string;
      slug: string;
      description?: string | null;
      icon?: string | null;
      color?: string | null;
      default_classification?: KbDataClassification;
    }) => {
      const { data, error } = await supabase.rpc("kb_create_space", {
        p_name: params.name,
        p_slug: params.slug,
        p_description: params.description ?? null,
        p_icon: params.icon ?? null,
        p_color: params.color ?? null,
        p_default_classification: params.default_classification ?? "internal",
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.spaces() });
      toast({ title: "Space criado com sucesso" });
    },
    onError: (err: any) => {
      logger.error("useCreateKbSpace", "erro", err);
      toast({
        title: "Erro ao criar space",
        description: err?.message ?? "Erro desconhecido",
        variant: "destructive",
      });
    },
  });
}

/** Atualiza campos opcionais de um space. */
export function useUpdateKbSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      space_id: string;
      name?: string;
      description?: string | null;
      icon?: string | null;
      color?: string | null;
      default_classification?: KbDataClassification;
    }) => {
      const { data, error } = await supabase.rpc("kb_update_space", {
        p_space_id: params.space_id,
        p_name: params.name ?? null,
        p_description: params.description ?? null,
        p_icon: params.icon ?? null,
        p_color: params.color ?? null,
        p_default_classification: params.default_classification ?? null,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.spaces() });
      toast({ title: "Space atualizado" });
    },
    onError: (err: any) => {
      logger.error("useUpdateKbSpace", "erro", err);
      toast({
        title: "Erro ao atualizar space",
        description: err?.message,
        variant: "destructive",
      });
    },
  });
}

// ===========================================================================
// COLLECTIONS
// ===========================================================================

/** Cria uma coleção (hierárquica) dentro de um space. */
export function useCreateKbCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      space_id: string;
      name: string;
      slug: string;
      parent_id?: string | null;
      icon?: string | null;
      color?: string | null;
      description?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("kb_create_collection", {
        p_space_id: params.space_id,
        p_name: params.name,
        p_slug: params.slug,
        p_parent_id: params.parent_id ?? null,
        p_icon: params.icon ?? null,
        p_color: params.color ?? null,
        p_description: params.description ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.pagesTree(vars.space_id) });
      toast({ title: "Coleção criada" });
    },
    onError: (err: any) => {
      logger.error("useCreateKbCollection", "erro", err);
      toast({
        title: "Erro ao criar coleção",
        description: err?.message,
        variant: "destructive",
      });
    },
  });
}

/** Lista todas as coleções de um space com contagem de páginas. */
export interface KbCollectionRow {
  id: string;
  parent_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  col_position: number;
  is_archived: boolean;
  page_count: number;
  updated_at: string;
}

export function useKbCollections(spaceId: string | null | undefined) {
  return useQuery({
    queryKey: kbKeys.collections(spaceId ?? ""),
    enabled: !!spaceId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_list_collections", {
        p_space_id: spaceId!,
        p_include_archived: false,
      });
      if (error) throw error;
      return (data ?? []) as KbCollectionRow[];
    },
    staleTime: 15_000,
  });
}

/** Atualiza campos de uma coleção. */
export function useUpdateKbCollection(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      collection_id: string;
      name?: string;
      slug?: string;
      description?: string | null;
      icon?: string | null;
      color?: string | null;
      parent_id?: string | null;
      clear_parent?: boolean;
      position?: number;
      is_archived?: boolean;
    }) => {
      const { error } = await supabase.rpc("kb_update_collection", {
        p_collection_id: params.collection_id,
        p_name: params.name ?? null,
        p_slug: params.slug ?? null,
        p_description: params.description ?? null,
        p_icon: params.icon ?? null,
        p_color: params.color ?? null,
        p_parent_id: params.parent_id ?? null,
        p_clear_parent: params.clear_parent ?? false,
        p_position: params.position ?? null,
        p_is_archived: params.is_archived ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.collections(spaceId) });
      qc.invalidateQueries({ queryKey: kbKeys.pagesTree(spaceId) });
      toast({ title: "Coleção atualizada" });
    },
    onError: (err: any) => {
      logger.error("useUpdateKbCollection", "erro", err);
      toast({ title: "Erro ao atualizar coleção", description: err?.message, variant: "destructive" });
    },
  });
}

/** Exclui uma coleção; opcionalmente move páginas para outra coleção. */
export function useDeleteKbCollection(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      collection_id: string;
      reassign_to_collection_id?: string | null;
    }) => {
      const { error } = await supabase.rpc("kb_delete_collection", {
        p_collection_id: params.collection_id,
        p_reassign_to_collection_id: params.reassign_to_collection_id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.collections(spaceId) });
      qc.invalidateQueries({ queryKey: kbKeys.pagesTree(spaceId) });
      qc.invalidateQueries({ queryKey: kbKeys.spaces() });
      toast({ title: "Coleção excluída" });
    },
    onError: (err: any) => {
      logger.error("useDeleteKbCollection", "erro", err);
      toast({ title: "Erro ao excluir coleção", description: err?.message, variant: "destructive" });
    },
  });
}

/** Reordena coleções irmãs (mesmo parent_id) atribuindo positions pela ordem. */
export function useReorderKbCollections(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      parent_id: string | null;
      ordered_ids: string[];
    }) => {
      const { error } = await supabase.rpc("kb_reorder_collections", {
        p_space_id: spaceId,
        p_parent_id: params.parent_id,
        p_ordered_ids: params.ordered_ids,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.collections(spaceId) });
    },
    onError: (err: any) => {
      logger.error("useReorderKbCollections", "erro", err);
      toast({ title: "Erro ao reordenar coleções", description: err?.message, variant: "destructive" });
    },
  });
}

/** Move uma página entre coleções e/ou hierarquia de páginas. */
export function useMoveKbPage(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      page_id: string;
      collection_id?: string | null;
      clear_collection?: boolean;
      parent_id?: string | null;
      clear_parent?: boolean;
      position?: number;
    }) => {
      const { error } = await supabase.rpc("kb_move_page", {
        p_page_id: params.page_id,
        p_collection_id: params.collection_id ?? null,
        p_clear_collection: params.clear_collection ?? false,
        p_parent_id: params.parent_id ?? null,
        p_clear_parent: params.clear_parent ?? false,
        p_position: params.position ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.pagesTree(spaceId) });
      qc.invalidateQueries({ queryKey: kbKeys.collections(spaceId) });
    },
    onError: (err: any) => {
      logger.error("useMoveKbPage", "erro", err);
      toast({ title: "Erro ao mover página", description: err?.message, variant: "destructive" });
    },
  });
}

// ===========================================================================
// PAGES — árvore, leitura, escrita, publicação
// ===========================================================================

/** Lista páginas de um space (opcionalmente filtradas por coleção). */
export function useKbPagesTree(spaceId: string | null | undefined, collectionId?: string | null) {
  return useQuery({
    queryKey: kbKeys.pagesTree(spaceId ?? "", collectionId ?? null),
    enabled: !!spaceId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_list_pages_tree", {
        p_space_id: spaceId!,
        p_collection_id: collectionId ?? null,
      });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

/** Retorna metadata + conteúdo da versão publicada (ou última draft). */
export function useKbPage(pageId: string | null | undefined) {
  return useQuery({
    queryKey: kbKeys.page(pageId ?? ""),
    enabled: !!pageId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_get_page", {
        p_page_id: pageId!,
      });
      if (error) throw error;
      // RPC retorna 1 linha
      return (data && data[0]) || null;
    },
    staleTime: 15_000,
  });
}

/** Cria página em draft + snapshot v1. */
export function useCreateKbPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      space_id: string;
      title: string;
      slug: string;
      content_markdown?: string | null;
      collection_id?: string | null;
      parent_id?: string | null;
      ai_visibility?: KbAiVisibility;
      data_classification?: KbDataClassification;
      summary?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("kb_create_page", {
        p_space_id: params.space_id,
        p_title: params.title,
        p_slug: params.slug,
        p_content_markdown: params.content_markdown ?? null,
        p_collection_id: params.collection_id ?? null,
        p_parent_id: params.parent_id ?? null,
        p_ai_visibility: params.ai_visibility ?? null,
        p_data_classification: params.data_classification ?? null,
        p_summary: params.summary ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.pagesTree(vars.space_id) });
      qc.invalidateQueries({ queryKey: kbKeys.spaces() });
      toast({ title: "Página criada" });
    },
    onError: (err: any) => {
      logger.error("useCreateKbPage", "erro", err);
      toast({
        title: "Erro ao criar página",
        description: err?.message,
        variant: "destructive",
      });
    },
  });
}

/** Cria nova versão + atualiza metadata da página. */
export function useUpdateKbPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      page_id: string;
      title?: string;
      content_markdown?: string | null;
      summary?: string | null;
      ai_visibility?: KbAiVisibility;
      data_classification?: KbDataClassification;
      change_summary?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("kb_update_page", {
        p_page_id: params.page_id,
        p_title: params.title ?? null,
        p_content_markdown: params.content_markdown ?? null,
        p_summary: params.summary ?? null,
        p_ai_visibility: params.ai_visibility ?? null,
        p_data_classification: params.data_classification ?? null,
        p_change_summary: params.change_summary ?? null,
      });
      if (error) throw error;
      return data as string; // version_id
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.page(vars.page_id) });
      qc.invalidateQueries({ queryKey: kbKeys.all });
      toast({ title: "Página atualizada" });
    },
    onError: (err: any) => {
      logger.error("useUpdateKbPage", "erro", err);
      toast({
        title: "Erro ao atualizar página",
        description: err?.message,
        variant: "destructive",
      });
    },
  });
}

/** Publica a última versão da página. */
export function usePublishKbPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pageId: string) => {
      const { data, error } = await supabase.rpc("kb_publish_page", {
        p_page_id: pageId,
      });
      if (error) throw error;

      // Sincroniza kb_page_links após publish (fundação wiki) — fire-and-forget.
      supabase.rpc("kb_sync_page_links", {
        p_page_id: pageId,
        p_tenant_hint: null,
      }).then(({ error: syncErr }) => {
        if (syncErr) logger.warn("usePublishKbPage", "kb_sync_page_links falhou", syncErr);
      });

      return data as string; // version_id publicado
    },
    onSuccess: (_data, pageId) => {
      qc.invalidateQueries({ queryKey: kbKeys.page(pageId) });
      qc.invalidateQueries({ queryKey: kbKeys.all });
      qc.invalidateQueries({ queryKey: ["kb", "backlinks"] });
      toast({ title: "Página publicada" });
    },
    onError: (err: any) => {
      logger.error("usePublishKbPage", "erro", err);
      toast({
        title: "Erro ao publicar",
        description: err?.message,
        variant: "destructive",
      });
    },
  });
}

// ===========================================================================
// SEARCH (FTS)
// ===========================================================================

/**
 * Busca full-text em português nas páginas publicadas.
 * `enabled` controlado por presença de query — evita chamada com string vazia.
 */
export function useKbSearch(query: string, spaceId?: string | null, limit = 20) {
  const trimmed = (query ?? "").trim();
  return useQuery({
    queryKey: kbKeys.search(trimmed, spaceId ?? null),
    enabled: trimmed.length >= 2,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_search_fts", {
        p_query: trimmed,
        p_space_id: spaceId ?? null,
        p_limit: limit,
      });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}
